import React, { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useStore } from 'effector-react';
import games, { getList } from '@stores/games';
import { Button, Spin, Table } from 'antd';
// import css from './GamesList.module.less';
import { ColumnsType, TablePaginationConfig } from 'antd/lib/table';
import IGame from '@interfaces/IGame';
import GameCard from '@components/GameCard';
import componentContent from './GameList.content.json';
import useTranslate from '@hooks/useTranslate';
import { EditOutlined } from '@ant-design/icons';
import { openModal } from '@stores/modals';
import { SortOrder } from 'antd/lib/table/interface';
import { pageSizeOptions } from '@constants/pagination';
import useScrollTopPage from '@hooks/useScrollTopPage';
import platforms from '@stores/platforms';
import useGuards from '@hooks/useGuards';

interface GamesListProps {}

const GamesList: FunctionComponent<GamesListProps> = () => {
  const { list, preloaders, pagination, filters: currentFilterParams } = useStore(games);
  const { basePlatforms, otherPlatforms } = useStore(platforms);
  const { allowChange } = useGuards();

  const [currentSortedColumnKey, setCurrentSortedColumnKey] = useState(
    (currentFilterParams.sort || '').split(',')[0],
  );
  const [currentSortDirection, setCurrentSortDirection] = useState(
    (currentFilterParams.sort || '').split(',')[1],
  );

  useEffect(() => {
    const [sortedColumnKey, sortDirection] = (currentFilterParams.sort || '').split(',');
    setCurrentSortedColumnKey(sortedColumnKey);
    setCurrentSortDirection(sortDirection);
  }, [currentFilterParams]);

  const onChange = useCallback(
    (pagination: TablePaginationConfig, filters: any, sorter: any) => {
      localStorage.setItem('gamesPageSize', `${pagination.pageSize}`);
      let sortParams = {};
      if (sorter.order === undefined) {
        sortParams = {
          columnKey: sorter.columnKey,
          order: currentSortDirection === 'desc' ? 'asc' : 'desc',
        };
      }
      getList({
        pagination,
        sorter: sorter.order ? sorter : sortParams,
        filters: {
          ...filters,
          filter: currentFilterParams.filter,
        },
      });
    },
    [currentSortDirection, currentFilterParams],
  );

  useScrollTopPage(pagination);

  const platformField = useCallback(
    (game: IGame) => {
      const filteredPlatforms = Object.entries({ ...basePlatforms, ...otherPlatforms }).filter(
        ([platformName, platformId]) => {
          return game.platforms.includes(platformId as number);
        },
      );
      return (
        <ul
          style={{
            padding: 0,
            listStyleType: 'none',
          }}
        >
          {filteredPlatforms.map(([platformName, platformId]) => (
            <li key={`${platformId}`}>{platformName}</li>
          ))}
        </ul>
      );
    },
    [basePlatforms, otherPlatforms],
  );

  const platformFilters = useCallback((): any[] => {
    return Object.entries({ ...basePlatforms, ...otherPlatforms }).map(
      ([platformName, platformId]) => {
        return {
          text: platformName,
          value: platformId,
        };
      },
    );
  }, [basePlatforms, otherPlatforms]);

  const content = useTranslate(componentContent);
  const columns: ColumnsType<IGame> = [
    {
      key: 'id',
      dataIndex: 'id',
      title: content.id,
      width: 56,
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'id' ? (`${currentSortDirection}end` as SortOrder) : undefined,
    },
    {
      key: 'name',
      dataIndex: 'name',
      title: content.name,
      render: (_, game) => (
        <GameCard
          game={game}
          mode='row'
        />
      ),
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'name' ? (`${currentSortDirection}end` as SortOrder) : undefined,
      showSorterTooltip: false,
    },
    {
      key: 'subscribers_count',
      dataIndex: 'subscribers_count',
      render: (_, game) => game.userCount,
      title: content.wasAdded,
      sorter: true,
      sortOrder:
        currentSortedColumnKey === 'subscribers_count'
          ? (`${currentSortDirection}end` as SortOrder)
          : undefined,
      showSorterTooltip: false,
    },
    {
      key: 'platforms',
      dataIndex: 'platforms',
      title: content.platform,
      render: (_, game) => platformField(game),
      filters: platformFilters(),
    },
    {
      key: 'actions',
      width: 64,
      render: (_, game) => {
        return allowChange ? (
          <Button
            onClick={() => openModal({ name: 'game', data: game })}
            icon={<EditOutlined />}
            shape='circle'
          />
        ) : null;
      },
    },
  ];

  return (
    <Spin spinning={!!preloaders.getList}>
      <Table
        rowKey='id'
        dataSource={list}
        columns={columns}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          pageSizeOptions,
          position: ['topRight', 'bottomRight'],
        }}
        onChange={onChange}
      />
    </Spin>
  );
};

export default GamesList;
